import { useEffect, useRef, useState } from "react";
import { ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FORMATS = ["code_128", "code_39", "ean_13", "ean_8", "qr_code", "data_matrix", "itf"];

const ScannerView = ({ onDetected, onError }) => {
  const video = useRef(null);
  useEffect(() => {
    let stream;
    let timer;
    let stopped = false;
    const start = async () => {
      try {
        const detector = new window.BarcodeDetector({ formats: FORMATS });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (stopped) return;
        video.current.srcObject = stream;
        await video.current.play();
        const tick = async () => {
          if (stopped) return;
          const codes = await detector.detect(video.current).catch(() => []);
          if (codes.length) onDetected(codes[0].rawValue.trim());
          else timer = setTimeout(tick, 250);
        };
        tick();
      } catch (e) {
        onError("Impossibile accedere alla fotocamera: inserisci il seriale a mano.");
      }
    };
    start();
    return () => { stopped = true; clearTimeout(timer); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onDetected, onError]);
  return <video ref={video} playsInline muted className="aspect-[4/3] w-full bg-black object-cover" data-testid="serial-scanner-video" />;
};

export const SerialScanButton = ({ onDetected, testId, title = "Scansiona seriale" }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const supported = "BarcodeDetector" in window && !!navigator.mediaDevices?.getUserMedia;
  const done = (code) => { onDetected(code); setOpen(false); };
  return (
    <>
      <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => { setError(""); setOpen(true); }} aria-label="Scansiona seriale" data-testid={testId}>
        <ScanLine className="h-5 w-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="serial-scanner-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle></DialogHeader>
          {!supported || error
            ? <p className="text-sm text-muted-foreground" data-testid="serial-scanner-unsupported">{error || "La scansione dalla fotocamera non è supportata da questo browser: inserisci il seriale a mano."}</p>
            : <><ScannerView onDetected={done} onError={setError} /><p className="text-xs text-muted-foreground">Inquadra il codice a barre o QR dell'etichetta.</p></>}
        </DialogContent>
      </Dialog>
    </>
  );
};
